import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Combine, Download, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dropzone } from "@/components/converter/Dropzone";
import { MergeList } from "@/components/merge/MergeList";
import { API } from "@/lib/api";
import { directDownload, filesForm, useFileQueue, useWindowDrop } from "@/lib/files";

const cleanName = (s) => (s.trim().replace(/\.pdf$/i, "").replace(/[\\/:*?"<>|]+/g, "_") || "pdf_uniti");

export default function MergePage() {
  const { queue, addFiles, removeAt, move, clear } = useFileQueue();
  const [name, setName] = useState("pdf_uniti");
  const [pct, setPct] = useState(null);
  const [ready, setReady] = useState(null);
  useEffect(() => setReady(null), [queue, name]);
  const busy = pct !== null;
  useWindowDrop(addFiles, !busy);

  const merge = async () => {
    setReady(null);
    setPct(0);
    try {
      const res = await axios.post(`${API}/merge/prepare`, filesForm(queue), {
        onUploadProgress: (ev) => ev.total && setPct(Math.round((ev.loaded / ev.total) * 100)),
      });
      const file = `${cleanName(name)}.pdf`;
      const url = `${API}/merge/${res.data.merge_id}/download?name=${encodeURIComponent(cleanName(name))}`;
      setReady({ url, file });
      directDownload(url);
      toast.success(`${queue.length} PDF uniti in ${file}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unione non riuscita, riprova.");
    } finally {
      setPct(null);
    }
  };

  return (
    <main data-testid="merge-page" className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <header className="mb-8 fade-up">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700"><Combine className="h-4 w-4" /> Strumento PDF</p>
        <h1 className="mt-3 font-display text-4xl font-bold text-slate-900 sm:text-5xl">Unisci file PDF</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-500 sm:text-base">Scegli due o più PDF, mettili nell'ordine che preferisci e scarica un unico file. Il PDF unito viene eliminato automaticamente entro un'ora.</p>
      </header>
      <Dropzone onFile={addFiles} compact={queue.length > 0} title="Trascina qui i PDF da unire"
        steps={["1 · Scegli due o più PDF", "2 · Mettili in ordine", "3 · Unisci e scarica un solo PDF"]} />
      {queue.length > 0 && (
        <section data-testid="merge-panel" className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 fade-up">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-slate-900">
              <span data-testid="merge-count">{queue.length}</span> PDF · ordine di unione
            </h2>
            <Button variant="ghost" size="sm" data-testid="merge-clear" onClick={clear} disabled={busy} className="text-slate-500">
              <Trash2 className="mr-1.5 h-4 w-4" /> Svuota
            </Button>
          </div>
          <MergeList files={queue} onMove={move} onRemove={removeAt} />
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex-1 text-xs font-medium text-slate-500">
              Nome del file unito
              <div className="mt-1 flex items-center gap-2">
                <Input data-testid="merge-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
                <span className="font-mono text-sm text-slate-400">.pdf</span>
              </div>
            </label>
            <Button data-testid="merge-submit-button" onClick={merge} disabled={busy || queue.length < 2}
              className="h-11 rounded-full bg-emerald-700 px-6 text-white hover:bg-emerald-800 btn-lift">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Combine className="mr-2 h-4 w-4" />}
              Unisci {queue.length} PDF e scarica
            </Button>
          </div>
          {queue.length < 2 && <p data-testid="merge-min-hint" className="mt-3 text-xs text-amber-700">Aggiungi almeno un altro PDF per poterli unire.</p>}
          {ready && !busy && (
            <div data-testid="merge-ready" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-sm text-emerald-800">PDF unito pronto. Se il download non è partito, premi il pulsante.</p>
              <a data-testid="merge-download-link" href={ready.url} download={ready.file}
                className="inline-flex h-10 items-center rounded-full bg-emerald-700 px-5 text-sm font-medium text-white hover:bg-emerald-800">
                <Download className="mr-2 h-4 w-4" /> Scarica {ready.file}
              </a>
            </div>
          )}
          {busy && (
            <div data-testid="merge-progress" className="mt-4 space-y-2">
              <Progress value={pct} className="h-2 bg-slate-100" />
              <p className="font-mono text-xs text-slate-500">{pct < 100 ? `Caricamento: ${pct}%` : "Unione in corso…"}</p>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
